import React, { useContext, useEffect, useRef, useState } from "react";
import GarageCard from "../garageCard";

import InfiniteScroll from "react-infinite-scroll-component";

import * as Styles from "./styles";
import { useRouter } from "next/router";
import { useOnload } from "@utils/useOnLoad";
import ImageContainer from "@components/imageContainer";
import { Context } from "@components/useContext/context";
import { BasicLoader } from "@acko-tech/building-blocks.ui.loaders";


interface IGarageListProps {
  lat: number;
  lng: number;
  make: string;
  city: string;
  distance: number;
  actions?: {
    on_load?: unknown[];
  };
  totalCount: number
  pincode: string
}

const GARAGE_SEARCH_RADIUS_KM = 50;

type GarageListRow = {
  id: string | number;
  name?: string;
  network_type?: string;
  garage_type?: string;
  distance?: number | string;
  metadata?: {
    rating?: number | string;
    address?: string;
    garage_type?: string;
    workshop_type?: string;
  };
};

type GarageListMeta = {
  offset: number;
  limit: number;
  type?: string | string[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function getStringValue(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function getNumberLikeValue(value: unknown): number | string | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) {
    return value;
  }
  return undefined;
}

function getGarageRow(row: unknown): GarageListRow | null {
  if (!isRecord(row)) return null;

  const { id } = row;
  if (typeof id !== "string" && typeof id !== "number") return null;

  const metadata = isRecord(row.metadata) ? row.metadata : {};

  return {
    id,
    name: getStringValue(row.name),
    network_type: getStringValue(row.network_type),
    garage_type: getStringValue(row.garage_type),
    distance: getNumberLikeValue(row.distance),
    metadata: {
      rating: getNumberLikeValue(metadata.rating),
      address: getStringValue(metadata.address),
      garage_type: getStringValue(metadata.garage_type),
      workshop_type: getStringValue(metadata.workshop_type)
    }
  };
}

function getGarageMeta(meta: unknown): Partial<GarageListMeta> {
  if (!isRecord(meta)) return {};

  const nextMeta: Partial<GarageListMeta> = {};
  const offset = Number(meta.offset);
  const limit = Number(meta.limit);

  if (Number.isFinite(offset)) nextMeta.offset = offset;
  if (Number.isFinite(limit)) nextMeta.limit = limit;
  if (typeof meta.type === "string" || Array.isArray(meta.type)) {
    nextMeta.type = meta.type;
  }

  return nextMeta;
}

function getGarageResponse(responseData: unknown): {
  data: GarageListRow[];
  meta: Partial<GarageListMeta>;
} {
  if (!isRecord(responseData)) {
    return { data: [], meta: {} };
  }

  return {
    data: Array.isArray(responseData.data)
      ? responseData.data
        .map(getGarageRow)
        .filter((garage): garage is GarageListRow => Boolean(garage))
      : [],
    meta: getGarageMeta(responseData.meta)
  };
}

function isDealershipGarage(garage: GarageListRow) {
  return [
    garage?.network_type,
    garage?.garage_type,
    garage?.metadata?.garage_type,
    garage?.metadata?.workshop_type
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase())
    .some((value) => value.includes("dealer"));
}

function sortGarages(garages: GarageListRow[]) {
  return [...(garages || [])].sort((a, b) => {
    const dealershipDelta = Number(!isDealershipGarage(a)) - Number(!isDealershipGarage(b));
    if (dealershipDelta !== 0) return dealershipDelta;
    const aDistance = Number.isFinite(Number(a?.distance)) ? Number(a.distance) : Number.MAX_SAFE_INTEGER;
    const bDistance = Number.isFinite(Number(b?.distance)) ? Number(b.distance) : Number.MAX_SAFE_INTEGER;
    return aDistance - bDistance;
  });
}

function getGarageListApiUrl() {
  const garageListService = process.env.GARAGE_LIST_SERVICE;
  const sduiApiService = process.env.SDUI_API_SERVICE;
  const apiUrl = garageListService || (sduiApiService ? `${sduiApiService}/garage` : "");

  if (/^(localhost|127\.0\.0\.1)(:|\/)/.test(apiUrl)) {
    return `http://${apiUrl}`;
  }

  return apiUrl;
}

export default function GarageList(props: IGarageListProps) {
  const { make, lat, lng, actions, totalCount, pincode
  } = props;
  const router = useRouter();
  const { isDesktop } = useContext(Context);


  const [garagesFound, setGargesfound] = useState(true);

  // const [sortTags, setSortTags] = useState<string[]>([]);
  const [showTopElement, setShowTopElement] = useState(false);
  const [garageData, setGarageData] = useState<GarageListRow[]>([]);
  const garageDataRef = useRef<GarageListRow[]>([]);
  const { current_node } = router?.query || {};

  const [currentMetaData, setcurrentMetaData] = useState<GarageListMeta>({
    offset: 0,
    limit: 10,
    type: current_node
  });

  const lastCardRef = useRef<HTMLDivElement>(null);

  const { on_load } = actions || {};

  useOnload({ on_load });

  useEffect(() => {
    fetchGarageData();
    const handleScroll = () => {
      setShowTopElement(window.scrollY >= 285);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const lastCardInView = (entries: IntersectionObserverEntry[]) => {
    if (entries?.[0]?.isIntersecting) {
      fetchGarageData();
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(lastCardInView, {
      threshold: 0.5
    });

    if (lastCardRef?.current) {
      observer.observe(lastCardRef.current);
    }

    return () => {
      if (lastCardRef?.current) {
        observer.unobserve(lastCardRef.current);
      }
    };
  }, []);

  const fetchGarageData = () => {
    try {
      const apiUrl = getGarageListApiUrl();
      if (!apiUrl) {
        if (!garageDataRef.current.length) {
          setGargesfound(false);
        }
        return;
      }

      const requestBody = {
        garageData: {
          reference_id: "firefly_admin",
          context: "firefly_admin",
          type: "garage",
          params: {
            sort_by: "distance",
            coordinate: {
              latitude: lat,
              longitude: lng
            },
            make,
            pincode,
            distance: GARAGE_SEARCH_RADIUS_KM
          }
        },
        meta: currentMetaData
      };
      fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(requestBody)
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Garage list request failed with status ${response.status}`);
          }
          return response.json();
        })
        .then((responseData) => {
          const { data, meta } = getGarageResponse(responseData);
          const rows = sortGarages(data);
          if (rows.length > 0) {
            setGargesfound(true);
          }
          setGarageData((currentdata) => {
            const nextData = sortGarages([...currentdata, ...rows]);
            garageDataRef.current = nextData;
            if (!nextData.length && !currentdata.length) {
              setGargesfound(false);
            }
            return nextData;
          });
          setcurrentMetaData((currentmeta) => ({ ...currentmeta, ...meta }));
        })
        .catch((error) => {
          console.error("Error fetching garage data:", error);
          if (!garageDataRef.current.length) {
            setGargesfound(false);
          }
        });
    } catch {
      //
    }
  };

  // const addSort = (tag: string) => {
  //     if (sortTags.includes(tag)) {
  //         setSortTags(sortTags.filter((t) => t !== tag));
  //     } else {
  //         setSortTags([...sortTags, tag]);
  //     }
  // };

  // const removeSort = (tag: string) => {
  //     setSortTags(sortTags.filter((t) => t !== tag));
  // };

  const onBackClick = () => {
    router.back();
    // Implement your back button functionality here
  };


  if (!garagesFound) {
    return (
      <Styles.NoGarageFoundWrapper
        width="100%"
        alignItems="center"
        justifyContent="center"
      >
        <ImageContainer
          imgSrc="https://storefront-consumer-assets.s3.ap-south-1.amazonaws.com/static/sdui-review/car_not_found.svg"
          width={72}
          height={72}
          alt="icon"
        />
        <Styles.NoGarageFound>No garages found</Styles.NoGarageFound>
      </Styles.NoGarageFoundWrapper>
    );
  }

  return (
    <>
      <Styles.ScrollContainer>
        {showTopElement && !isDesktop && (
          <Styles.Header>
            <Styles.BackIcon
              src="https://storefront-consumer-assets.s3.ap-south-1.amazonaws.com/static/document-service/6708d9aa068cc2696517bff8.png"
              onClick={onBackClick}
            />
            <Styles.Location
              src="https://storefront-consumer-assets.s3.ap-south-1.amazonaws.com/static/document-service/6708daee068cc2696517c2f1.png"
              width={20}
              height={20}
            />
            <Styles.City>{pincode}</Styles.City>
          </Styles.Header>
        )}
        {/* {!!sortTagsList?.length && (
                    <Styles.FilterWrapper showTopElement={showTopElement}>
                        {sortTagsList?.map((tag) => (
                            <Styles.Tag
                                key={tag}
                                className={clsx({
                                    selected: sortTags.includes(tag),
                                })}
                                onClick={() => addSort(tag)}
                            >
                                {tag}
                                {sortTags.includes(tag) && (
                                    <span
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            removeSort(tag);
                                        }}
                                    />
                                )}
                            </Styles.Tag>
                        ))}
                    </Styles.FilterWrapper>
                )} */}
        <Styles.ListWrapper>
          <InfiniteScroll
            dataLength={garageData?.length}
            next={() => fetchGarageData()}
            hasMore={garageData?.length < totalCount}
            loader={
              <Styles.LoadingMoreGarages>
                <BasicLoader />
              </Styles.LoadingMoreGarages>
            }
            endMessage={
              <Styles.LoadingMoreGarages>
              </Styles.LoadingMoreGarages>
            }
          >
            {garageData?.map((g, index: number) => {
              const getGarageTab = () => {
                if (g.network_type?.includes("dealer")) {
                  return [`${make} dealership`]
                }
                return []

              }
              return g?.id ? (
                <div
                  key={g.id}
                  data-testid="garage-list-card"
                  data-garage-type={isDealershipGarage(g) ? "dealership" : "network"}
                  data-distance={g?.distance ?? ""}
                  ref={index === garageData?.length - 1 ? lastCardRef : null}
                >
                  <GarageCard
                    key={g.id}
                    tags={(getGarageTab())}
                    garageName={g.name || ""}
                    rating={g.metadata?.rating != null ? String(g.metadata.rating) : ""}
                    distance={g.distance != null ? String(g.distance) : ""}
                    address={g.metadata?.address || ""}
                  />
                </div>
              ) : null;
            })}
          </InfiniteScroll>
        </Styles.ListWrapper>
      </Styles.ScrollContainer>
    </>
  );
}
